/**
 * Pure edits to the pending (unapproved) presentation draft.
 * They only change the draft object. Library slides and images a draft points
 * to are Microlab resources; removing them from a draft never deletes them.
 */

let seq = 0;
function newSlideId() {
  seq += 1;
  return `pending-c${Date.now().toString(36)}-${seq}`;
}

export function isMediaSlide(slide) {
  return (
    slide?.slide_kind === "media" ||
    slide?.is_media_slide === true ||
    (Boolean(slide?.related_3d?.modelUrl || slide?.related_3d?.imageUrl) &&
      !(Array.isArray(slide?.bullets) && slide.bullets.length > 0))
  );
}

function withSlides(pending, slides) {
  const ordered = slides.map((s, i) => ({ ...s, id: s.id || newSlideId(), order: i + 1 }));
  return { ...pending, slides: ordered, slide_count: ordered.length };
}

function inRange(pending, index) {
  return Number.isInteger(index) && index >= 0 && index < (pending?.slides?.length || 0);
}

export function cleanBullets(list) {
  return (Array.isArray(list) ? list : [])
    .map((b) => String(b || "").replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean)
    .slice(0, 8);
}

export function updateSlide(pending, index, patch = {}) {
  if (!inRange(pending, index)) return pending;
  const slides = [...pending.slides];
  const next = { ...slides[index] };
  if (patch.title !== undefined) next.title = String(patch.title || "").slice(0, 140) || next.title;
  if (patch.bullets !== undefined) next.bullets = cleanBullets(patch.bullets);
  if (patch.speaker_notes !== undefined) next.speaker_notes = String(patch.speaker_notes || "");
  if (patch.supporting_image !== undefined) next.supporting_image = patch.supporting_image || null;
  if (patch.related_3d !== undefined) next.related_3d = patch.related_3d || null;
  slides[index] = next;
  return withSlides(pending, slides);
}

/** Returns the same object when it would leave the deck empty. */
export function removeSlide(pending, index) {
  if (!inRange(pending, index) || pending.slides.length <= 1) return pending;
  return withSlides(
    pending,
    pending.slides.filter((_, i) => i !== index)
  );
}

export function duplicateSlide(pending, index) {
  if (!inRange(pending, index)) return pending;
  const source = pending.slides[index];
  const copy = { ...source, id: null, title: `${source.title || "Slide"} (copy)` };
  const slides = [...pending.slides];
  slides.splice(index + 1, 0, copy);
  return withSlides(pending, slides);
}

export function moveSlide(pending, from, to) {
  if (!inRange(pending, from)) return pending;
  const target = Math.max(0, Math.min(pending.slides.length - 1, to));
  if (target === from) return pending;
  const slides = [...pending.slides];
  const [item] = slides.splice(from, 1);
  slides.splice(target, 0, item);
  return withSlides(pending, slides);
}

export function addSlide(pending, afterIndex = null, slide = {}) {
  const fresh = {
    id: null,
    title: slide.title || "New slide",
    bullets: cleanBullets(slide.bullets || []),
    speaker_notes: slide.speaker_notes || "",
    slide_kind: "content",
  };
  const slides = [...(pending?.slides || [])];
  const at = afterIndex == null ? slides.length : Math.max(0, Math.min(slides.length, afterIndex + 1));
  slides.splice(at, 0, fresh);
  return withSlides(pending || {}, slides);
}

/** Library item (from the presentation library search) in the shape the backend renders. */
export function libraryMedia(item) {
  if (!item) return null;
  return {
    label: item.label,
    modelUrl: item.modelUrl || null,
    imageUrl: item.imageUrl || null,
    tileUrl: item.tileUrl || null,
    thumbnailUrl: item.thumbnailUrl || null,
    kind: item.kind || (item.modelUrl ? "3d" : "slide"),
    title_id: item.title_id || null,
    slide_id: item.slide_id || null,
    image: item.image || null,
  };
}

/** Put a histology slide image or 3D model on a content slide (one visual per slide). */
export function setSlideVisual(pending, index, item) {
  if (!inRange(pending, index)) return pending;
  return updateSlide(pending, index, { related_3d: libraryMedia(item), supporting_image: null });
}

export function clearSlideImage(pending, index) {
  if (!inRange(pending, index)) return pending;
  const slide = pending.slides[index];
  const keep3d = slide.related_3d?.modelUrl ? slide.related_3d : null;
  return updateSlide(pending, index, { supporting_image: null, related_3d: keep3d });
}

export function clearSlide3d(pending, index) {
  if (!inRange(pending, index)) return pending;
  const slide = pending.slides[index];
  const media = slide.related_3d;
  if (!media?.modelUrl) return pending;
  return updateSlide(pending, index, { related_3d: media.imageUrl || media.tileUrl ? { ...media, modelUrl: null, kind: "slide" } : null });
}

/**
 * Layouts the existing presentation renderer supports for a content slide:
 *  - "visual": text with the image / 3D on the side (default when a visual exists)
 *  - "text": text only (visual removed from this draft slide)
 */
export function slideLayout(slide) {
  if (isMediaSlide(slide)) return "media";
  return slide?.supporting_image?.url || slide?.related_3d ? "visual" : "text";
}

export function setTextOnly(pending, index) {
  if (!inRange(pending, index)) return pending;
  return updateSlide(pending, index, { supporting_image: null, related_3d: null });
}

/** Add a library item as its own full-visual slide. */
export function addMediaSlide(pending, item, afterIndex = null) {
  const media = libraryMedia(item);
  if (!media) return pending;
  const slides = [...(pending?.slides || [])];
  const at = afterIndex == null ? slides.length : afterIndex + 1;
  slides.splice(at, 0, {
    id: null,
    title: media.label || "Library slide",
    bullets: [],
    speaker_notes: "",
    slide_kind: "media",
    is_media_slide: true,
    related_3d: media,
  });
  return withSlides(pending || {}, slides);
}

/** Cover thumb for a slide's visual. Never returns a raw tile root (shows as broken). */
export function slideThumb(slide) {
  if (slide?.supporting_image?.url) return slide.supporting_image.url;
  const media = slide?.related_3d || {};
  if (media.thumbnailUrl) return media.thumbnailUrl;
  const isStatic = (u) => /\.(png|jpe?g|webp|gif)(\?|#|$)/i.test(String(u || ""));
  if (isStatic(media.image)) return media.image;
  if (isStatic(media.imageUrl)) return media.imageUrl;
  const tile = media.tileUrl || media.imageUrl;
  if (!tile) return null;
  if (String(tile).includes("{z}")) {
    return String(tile).replace(/\/+$/, "").replace(/\/\{z\}\/\{x\}\/\{y\}\.[a-z0-9]+$/i, "/0/0/0.png");
  }
  return `${String(tile).replace(/\/+$/, "")}/0/0/0.png`;
}
